import type { ReactNode } from 'react';

interface CollapsibleSectionProps {
  title: string;
  badge?: string | number;
  isExpanded: boolean;
  onToggle: () => void;
  children: ReactNode;
  /** Column span for grid layouts (default: 1) */
  span?: 1 | 2 | 3 | 'full';
}

/**
 * A collapsible section with smooth expand/collapse animation.
 * Used in Play Mode for organizing character information.
 */
export function CollapsibleSection({
  title,
  badge,
  isExpanded,
  onToggle,
  children,
  span = 1,
}: CollapsibleSectionProps) {
  // Map span to tailwind classes for responsive grid layouts
  const spanClasses = {
    1: '',
    2: 'lg:col-span-2',
    3: 'xl:col-span-3',
    full: 'lg:col-span-2 xl:col-span-3',
  };

  return (
    <div
      className={`bg-background-card border border-background-elevated rounded-lg overflow-hidden
        hover:border-accent-bronze/30 transition-colors ${spanClasses[span]}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 sm:p-4
          hover:bg-background-elevated/50 transition-colors group"
      >
        <span
          className="font-display text-sm text-accent-gold uppercase tracking-wider
            group-hover:text-accent-gold-light transition-colors"
        >
          {title}
        </span>
        <div className="flex items-center gap-2">
          {badge !== undefined && (
            <span
              className="text-xs text-text-muted bg-background-elevated px-2 py-0.5 rounded
                border border-background-primary/50"
            >
              {badge}
            </span>
          )}
          <span
            className={`text-text-muted text-xs transition-transform duration-200
              ${isExpanded ? 'rotate-90' : ''}`}
          >
            ▶
          </span>
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out
          ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-3 pb-3 sm:px-4 sm:pb-4">{children}</div>
      </div>
    </div>
  );
}
